import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  releaseTemplateSwitchSchema,
  type ReleaseTemplateSwitchFormData,
} from '../../schema/releaseTemplateSwitchSchema';
import { useModal } from '../../contexts/ModalContext';
import ConfirmationModal from '../modals/ConfirmationModal';
import type { useFeedTemplateSwitch } from '../../hooks/useFeedTemplateSwitch';

type Props = Pick<
  ReturnType<typeof useFeedTemplateSwitch>,
  'operation' | 'release' | 'releaseError' | 'releasePending'
>;

export const ReleaseTemplateSwitch = ({ operation, release, releaseError, releasePending }: Props) => {
  const { openModal, closeModal } = useModal();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReleaseTemplateSwitchFormData>({
    resolver: zodResolver(releaseTemplateSwitchSchema),
    defaultValues: { reason: '' },
  });
  const onSubmit = handleSubmit(({ reason }) => {
    if (!operation) return;
    openModal(
      <ConfirmationModal
        title='Release lock after stopping the worker and reconciling state?'
        actionButtonLabel='Release lock'
        actionButtonClose='Cancel'
        closeModal={closeModal}
        handleConfirm={() => {
          void release({ id: operation.id, reason }).catch(() => undefined);
        }}
      />
    );
  });
  return (
    <Stack component='form' onSubmit={onSubmit} spacing={2}>
      <Typography variant='subtitle1'>Reconcile retained switch lock</Typography>
      <Alert severity='warning'>
        Stop the old switch worker and reconcile the actual provider, feed flags and fixtures queue before releasing
        this lock. Releasing does not cancel provider work or change feed settings.
      </Alert>
      <TextField
        label='Reconciliation notes'
        multiline
        minRows={2}
        {...register('reason')}
        error={Boolean(errors.reason)}
        helperText={errors.reason?.message}
        disabled={releasePending}
      />
      {releaseError && (
        <Alert severity='error'>Lock release was not confirmed. Refresh status before trying again.</Alert>
      )}
      <Button type='submit' variant='outlined' disabled={releasePending}>
        Review lock release
      </Button>
    </Stack>
  );
};
